import axios from "axios";

import { TRADING_API_URL } from "../../config/apiConfig";
import { negotiationApiOf } from "../negotiation/negotiationPayload";

/**
 * Stocks des négociants (page /traders) et demandes d'achat — sales-and-market-tracking.
 *
 *   Navigateur :3000 → Gateway :8080 → sales-and-market-tracking :2001
 *   GET  /traders/stocks?country=TG&subCategory=&page=&size=          public, sans JWT
 *   POST /traders/stocks/{id}/purchase-requests?country=TG            utilisateur connecté
 *   GET  /traders/purchase-requests/sent?country=TG                   utilisateur connecté
 *   POST /traders/purchase-requests/{id}/withdraw?country=TG          utilisateur connecté
 *   …    /traders/purchase-requests/{id}/negotiation?country=TG       utilisateur connecté
 *
 * `country` est le pays du stock (pays d'activité choisi sur le site) : la demande est
 * enregistrée dans la base de ce pays. Prix et devise viennent du backend (devise du pays du
 * négociant) ; le frontend ne connaît aucun prix.
 */
// La liste publique n'envoie jamais de JWT : un jeton expiré la ferait échouer en 401.
const publicApi = axios.create({ baseURL: TRADING_API_URL, timeout: 20000 });
const api = axios.create({ baseURL: TRADING_API_URL, timeout: 20000 });

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

/** @param {{country: string, subCategory?: string, page?: number, size?: number}} query */
export const listTraderStocks = async ({ country, subCategory, page = 0, size = 12 }) => {
    const { data } = await publicApi.get("/traders/stocks", {
        params: { country, subCategory: subCategory || undefined, page, size }
    });
    return {
        items: data?.items || [],
        page: data?.page ?? page,
        size: data?.size ?? size,
        total: data?.total ?? 0
    };
};

/**
 * @param {{fullName: string, companyName?: string, telephone?: string, email?: string,
 *          requestedQuantity: number, desiredDeliveryDate?: string, deliveryCity?: string,
 *          message?: string}} request
 */
export const requestPurchase = async (country, stockId, request) => {
    const { data } = await api.post(`/traders/stocks/${stockId}/purchase-requests`, request, {
        params: { country }
    });
    return data;
};

export const listSentPurchaseRequests = async (country) => {
    const { data } = await api.get("/traders/purchase-requests/sent", { params: { country } });
    return Array.isArray(data) ? data : [];
};

export const withdrawPurchaseRequest = async (country, requestId) => {
    const { data } = await api.post(`/traders/purchase-requests/${requestId}/withdraw`, null, {
        params: { country }
    });
    return data;
};

/** Négociation d'une demande envoyée (voir negotiationPayload). */
export const tradingNegotiationApi = negotiationApiOf(api, "/traders/purchase-requests");

const tradingApi = { listTraderStocks, requestPurchase, listSentPurchaseRequests, withdrawPurchaseRequest };

export default tradingApi;
